import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ArrowRight, Building2, CheckCircle, KeyRound, Lock, Mail, ShieldAlert, User } from 'lucide-react';
import pulseLogo from '../assets/pulse-logo.png';

export function ActivatePage() {
  const [licenseKey, setLicenseKey] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [adminFullName, setAdminFullName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { registerOrg } = useAuth();
  const navigate = useNavigate();

  const handleFormatKey = (val: string) => {
    // Automatically uppercase and clean
    setLicenseKey(val.toUpperCase().trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await registerOrg({
        organizationName,
        adminEmail,
        adminPassword,
        adminFullName,
        licenseKey,
      });
      navigate('/admin');
    } catch (err: any) {
      setError(err.message || 'Failed to activate organization license.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090909] flex flex-col justify-center items-center px-4 py-12 selection:bg-pulse-blue selection:text-white">
      <div className="w-full max-w-lg">
        {/* Back Link */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to Login</span>
          </Link>
        </div>

        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src={pulseLogo} alt="CK Pulse" className="w-16 h-16 object-contain drop-shadow-2xl" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">Activate CK Pulse</h1>
          <p className="text-xs text-zinc-400 mt-1">
            Redeem your cryptographic license key and provision your organization workspace
          </p>
        </div>

        {/* Activation Card */}
        <div className="bg-[#121212] border border-[#222222] rounded-2xl p-7 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-900/60 text-xs text-rose-300 flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1.5">
                License Activation Key
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                  <KeyRound className="w-4 h-4 text-pulse-blue" />
                </div>
                <input
                  type="text"
                  required
                  value={licenseKey}
                  onChange={(e) => handleFormatKey(e.target.value)}
                  placeholder="CK-XXXX-XXXX-XXXX"
                  className="w-full pl-9 pr-3 py-2 text-xs font-mono rounded-xl pulse-input tracking-wider uppercase"
                />
              </div>
              <p className="mt-1 text-[11px] text-zinc-500">
                Provided by CK Ecosystem platform administrator (7 Days, 1 Month, 1 Year, or Lifetime).
              </p>
            </div>

            <div className="pt-2 border-t border-[#222222]">
              <label className="block text-xs font-medium text-zinc-300 mb-1.5">
                Organization / Company Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                  <Building2 className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  required
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="Acme Security Ltd."
                  className="w-full pl-9 pr-3 py-2 text-xs rounded-xl pulse-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1.5">
                Administrator Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                  <User className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  required
                  value={adminFullName}
                  onChange={(e) => setAdminFullName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full pl-9 pr-3 py-2 text-xs rounded-xl pulse-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-300 mb-1.5">
                  Admin Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    required
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@acme.com"
                    className="w-full pl-9 pr-3 py-2 text-xs rounded-xl pulse-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-zinc-300 mb-1.5">
                  Admin Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    className="w-full pl-9 pr-3 py-2 text-xs rounded-xl pulse-input"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-2.5 px-4 text-xs font-medium rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Activate Subscription & Launch Workspace</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
